import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import React from "react"
import { TimePicker } from "../components/ui/DateTimePickers"
import { LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import dayjs from "dayjs"
import { useState, useEffect } from "react"
import { ExpandMore } from "@mui/icons-material"
import Timetable from "../components/common/Timetable"

const NewSchedule = () => {
  const [classStart, setClassStart] = useState(null)
  const [classEnd, setClassEnd] = useState(null)
  const [breakStart, setBreakStart] = useState(null)
  const [breakEnd, setBreakEnd] = useState(null)
  const [durationHrs, setDurationHrs] = useState(0)
  const [durationMins, setDurationMins] = useState(0)
  const [error, setError] = useState(false)

  const [schedule, setSchedule] = useState([[], [], [], [], [], []])
  const [timings, setTimings] = useState([])

  const createSchedule = () => {
    setError(false)
    setSchedule([[], [], [], [], [], []])
    setTimings([])
    let timingsTempBefore = []
    let timingsTempAfter = []
    let currTime = dayjs(classStart)
    const breakS = dayjs(breakStart)
    while (true) {
      const startTime = currTime.format("HH:mm")
      currTime = currTime.add(durationHrs, "hour").add(durationMins, "minute")
      const endTime = currTime.format("HH:mm")
      if (currTime.isAfter(breakS, "minute")) {
        setError(true)
        return
      } else {
        timingsTempBefore.push(startTime + " - " + endTime)
        if (currTime.isSame(breakS, "minute")) break
      }
    }

    currTime = dayjs(breakEnd)
    const classE = dayjs(classEnd)

    while (true) {
      const startTime = currTime.format("HH:mm")
      currTime = currTime.add(durationHrs, "hour").add(durationMins, "minute")
      const endTime = currTime.format("HH:mm")
      if (currTime.isAfter(classE, "minute")) {
        setError(true)
        return
      } else {
        timingsTempAfter.push(startTime + "-" + endTime)
        if (currTime.isSame(classE, "minute")) break
      }
    }

    setTimings([
      ...timingsTempBefore,
      breakStart.format("HH:mm") + "-" + breakEnd.format("HH:mm"),
      ...timingsTempAfter,
    ])
    setSchedule(
      schedule.map(() => [
        ...timingsTempBefore.map(() => ({ name: "" })),
        { name: "BREAK" },
        ...timingsTempAfter.map(() => ({ name: "" })),
      ])
    )
  }

  const [classTimes, setClassTimes] = useState({})

  const days = ["MON", "TUE", "WED", "THU", "FRI", "SAT"]

  const getClassTimes = () => {
    let temp = {}
    schedule.forEach((day, i) => {
      day.forEach((c, j) => {
        if (c.name) {
          if (c.name in temp) {
            temp[c.name].push(days[i] + " | " + timings[j])
          } else {
            temp[c.name] = [days[i] + " | " + timings[j]]
          }
        }
      })
    })
    setClassTimes(temp)
  }

  useEffect(() => {
    getClassTimes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule])

  const setClassName = (i, j, name) => {
    setSchedule([
      ...schedule.slice(0, i),
      [
        ...schedule[i].slice(0, j),
        {
          ...schedule[i][j],
          name,
        },
        ...schedule[i].slice(j + 1),
      ],
      ...schedule.slice(i + 1),
    ])
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction={{ xs: "column", sm: "row" }} gap={{ xs: 1, sm: 5 }}>
        <Box>
          <Typography sx={{ mb: 2 }}>Academic Period</Typography>
          <Stack direction="row" gap={2}>
            <TimePicker
              value={classStart}
              onChange={(newValue) => {
                setClassStart(newValue)
              }}
              ampm={false}
              label="Start time"
              maxTime={classEnd}
            />
            <TimePicker
              disabled={!classStart}
              value={classEnd}
              onChange={(newValue) => {
                setClassEnd(newValue)
              }}
              ampm={false}
              label="End time"
              minTime={classStart}
            />
          </Stack>
        </Box>
        <Box>
          <Typography sx={{ mb: 2 }}>Break Period</Typography>
          <Stack direction="row" gap={2}>
            <TimePicker
              disabled={!classEnd}
              value={breakStart}
              onChange={(newValue) => {
                setBreakStart(newValue)
              }}
              ampm={false}
              label="Start time"
              minTime={classStart}
              maxTime={breakEnd}
            />
            <TimePicker
              disabled={!breakStart}
              value={breakEnd}
              onChange={(newValue) => {
                setBreakEnd(newValue)
              }}
              ampm={false}
              label="End time"
              minTime={breakStart}
              maxTime={classEnd}
            />
          </Stack>
        </Box>
        <Box>
          <Typography sx={{ mb: 2 }}>Class duration</Typography>
          <Stack direction="row" gap={2}>
            <TextField
              value={durationHrs}
              onChange={(e) => setDurationHrs(parseInt(e.target.value))}
              type="number"
            />{" "}
            hours
            <TextField
              value={durationMins}
              onChange={(e) => setDurationMins(parseInt(e.target.value))}
              type="number"
            />{" "}
            mins
          </Stack>
        </Box>
      </Stack>
      <Box sx={{ mt: 3 }}>
        <Button
          fullWidth
          disabled={
            !(
              classStart &&
              classEnd &&
              breakStart &&
              breakEnd &&
              (durationHrs || durationMins)
            )
          }
          onClick={createSchedule}
          variant="contained"
        >
          Create schedule
        </Button>
        {error && <p>Invalid Input!</p>}
      </Box>

      {!!timings.length && (
        <>
          <Grid container sx={{ mt: 3 }} spacing={2}>
            <Grid item xs={12} sm={9}>
              <Timetable
                timings={timings}
                schedule={schedule}
                setClassName={setClassName}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              {Object.entries(classTimes)
                .filter((c) => c[0] !== "BREAK")
                .map((c) => (
                  <Accordion>
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{ px: 2 }}
                    >
                      <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                        {c[0]}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack direction="row" flexWrap="wrap" gap={1}>
                        {c[1].map((d) => (
                          <Chip label={d} />
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                ))}
            </Grid>
          </Grid>
        </>
      )}
    </LocalizationProvider>
  )
}

export default NewSchedule
